import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { notifyStockAvailablePath } from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import { style } from './back-in-stock-notification.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PaperVariant } from 'components/shared/paper/paper.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'

export const backInStockNotificationDataSources = ['attributes', 'pdp-product'] as const
export type BackInStockNotificationDataSource = typeof backInStockNotificationDataSources[number]

const translations = {
  heading: () => t('heading', {
    ns: 'csc-back-in-stock-notification',
    default: 'Notify me when this product is back in stock',
  }),
  description: () => t('description', {
    ns: 'csc-back-in-stock-notification',
    default: 'Unfortunately, this product is currently out of stock. Enter your email address and receive a notification when this product is back in stock. Or check the product page for alternatives.',
  }),
  label: () => t('label', {
    ns: 'csc-back-in-stock-notification',
    default: 'E-mailaddress',
  }),
  emailPlaceholder: () => t('email_placeholder', {
    ns: 'csc-back-in-stock-notification',
    default: 'example@email.com',
  }),
  buttonText: () => t('button', {
    ns: 'csc-back-in-stock-notification',
    default: 'Mail me',
  }),
}

@customElement('csc-back-in-stock-notification')
export class CSCBackInStockNotification extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  @property({ type: String, attribute: 'data-source' })
  dataSource: BackInStockNotificationDataSource = 'attributes'

  @property({ type: String, attribute: 'paper-variant' })
  paperVariant: PaperVariant | '' = ''

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @state()
  private pdpProduct: PdpProduct | null = null

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.pdpProduct = product
    })
  }

  private get usesPdpProduct(): boolean {
    return this.dataSource === 'pdp-product'
  }

  private get activeProductId(): number | null {
    return this.productId ?? (this.usesPdpProduct ? this.pdpProduct?.id ?? null : null)
  }

  @state()
  private notifyUrl = ''

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('productId') || changedProperties.has('pdpProduct') || changedProperties.has('dataSource')) {
      void this.resolveNotifyUrl()
    }
  }

  private async resolveNotifyUrl() {
    const productId = this.activeProductId
    const url = productId === null ? '' : (await createUrl(notifyStockAvailablePath(productId))).href
    if (productId === this.activeProductId) this.notifyUrl = url
  }

  override render() {
    if (this.usesPdpProduct && !this.pdpProduct?.status?.email) return nothing
    if (!this.paperVariant) return this.renderNotification()
    return html`<cse-paper variant="${this.paperVariant}">${this.renderNotification()}</cse-paper>`
  }

  private renderNotification() {
    return html`
      <div class="back-in-stock-notification">
          <cse-text as="h6" variant="heading-6">${translations.heading()}</cse-text>
          <div class="content">
              <cse-text>${translations.description()}</cse-text>

              <form method="post" action="${this.notifyUrl}">
                <cse-form-field>
                  <cse-label slot="label">${translations.label()}</cse-label>
                  <cse-button-input placeholder=${translations.emailPlaceholder()}>
                    <span slot="button-text">${translations.buttonText()}</span>
                  </cse-button-input>
                </cse-form-field>
              </form>
          </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-back-in-stock-notification': CSCBackInStockNotification
  }
}
